/**
 * Truncated flow item cell for stats table (path, lander, offer).
 * Single line: "ID" (click to copy) + name. Responsive: full name on larger screens, truncate on small.
 * Hover: ID, Name, Tags (StatsHoverPopover). For Media Devoted | Created By Ciro Autiero
 */

import type { FC } from 'react';

import { LeadsListTags } from '@/features/smswiser-campaigns/components/shared';
import { useToast } from '@/providers/ToastProvider';

import {
  StatsHoverPopover,
  StatsTooltipSection,
  StatsTooltipTagsBlock,
} from '../../../StatsHoverPopover';

interface TruncatedFlowItemCellProps {
  itemId?: string | null;
  label: string;
  itemType?: 'path' | 'lander' | 'offer';
  /** Performance tags from path/lander/offer-performance-tags API */
  performanceTags?: string[];
  /** Tag + date range for hover tooltip */
  performanceTagDetails?: Array<{ tag: string; dateRangePreset: string }>;
  /** Custom colors for tags (rule name -> hex) */
  tagColors?: Record<string, string>;
  /** Flow Optimizer ranking tags (separate system — raw-score based). */
  flowTags?: string[];
  /** Colors for flow tags (rule name -> hex). */
  flowTagColors?: Record<string, string>;
}

export const TruncatedFlowItemCell: FC<TruncatedFlowItemCellProps> = ({
  itemId,
  label,
  itemType = 'path',
  performanceTags,
  performanceTagDetails,
  tagColors,
  flowTags,
  flowTagColors,
}) => {
  const { success: toastSuccess, error: toastError } = useToast();
  const displayName = label || itemId || '—';
  const typeLabel = itemType.charAt(0).toUpperCase() + itemType.slice(1);
  const hasCopyableValue = displayName && displayName !== '—';

  const handleCopyId = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!itemId) return;
    navigator.clipboard
      .writeText(itemId)
      .then(() => toastSuccess(`${typeLabel} ID copied`, 'Copied to clipboard'))
      .catch(() => toastError('Copy failed', 'Could not copy to clipboard'));
  };

  const handleCopyName = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!displayName || displayName === '—') return;
    navigator.clipboard
      .writeText(displayName)
      .then(() => toastSuccess(`${typeLabel} name copied`, 'Copied to clipboard'))
      .catch(() => toastError('Copy failed', 'Could not copy to clipboard'));
  };

  const tagsLine = (performanceTags?.length ?? 0) > 0 ? (
    <LeadsListTags
      performanceTags={performanceTags ?? []}
      tagColors={tagColors}
      className="inline-flex ml-1"
      compact
      disableTooltip
    />
  ) : null;

  const flowTagsLine = (flowTags?.length ?? 0) > 0 ? (
    <span className="ml-1 inline-flex flex-wrap items-center gap-1 align-middle">
      {flowTags!.map((tag) => {
        const color = flowTagColors?.[tag];
        return (
          <span
            key={tag}
            className="inline-flex items-center gap-0.5 rounded-full border px-1.5 text-[10px] font-medium leading-none"
            style={
              color
                ? { backgroundColor: `${color}22`, color, borderColor: `${color}55` }
                : undefined
            }
            title={`Flow optimisation tag: ${tag}`}
          >
            <span aria-hidden>⚡</span>
            {tag}
          </span>
        );
      })}
    </span>
  ) : null;

  const hoverContent = (
    <div className="flex flex-col gap-1.5 text-left">
      <StatsTooltipSection label="ID" valueClassName="font-mono">
        {itemId || '—'}
      </StatsTooltipSection>
      <StatsTooltipSection label="Name" valueClassName="font-medium">
        {displayName || '—'}
      </StatsTooltipSection>
      {((performanceTagDetails?.length ?? performanceTags?.length) ?? 0) > 0 && (
        <div>
          <StatsTooltipTagsBlock
            performanceTags={performanceTags}
            performanceTagDetails={performanceTagDetails}
            tagColors={tagColors}
          />
        </div>
      )}
      {(flowTags?.length ?? 0) > 0 && (
        <StatsTooltipSection label="Flow optimisation tags">
          <span className="inline-flex flex-wrap gap-1">
            {flowTags!.map((tag) => (
              <span
                key={tag}
                className="rounded px-1.5 py-0.5 text-[11px] font-medium"
                style={
                  flowTagColors?.[tag]
                    ? { backgroundColor: `${flowTagColors[tag]}26`, color: flowTagColors[tag] }
                    : undefined
                }
              >
                ⚡ {tag}
              </span>
            ))}
          </span>
        </StatsTooltipSection>
      )}
    </div>
  );

  return (
    <StatsHoverPopover content={hoverContent}>
      <div className="flex min-w-0 items-baseline gap-1.5 text-xs text-muted-foreground cursor-default">
        {itemId ? (
          <button
            type="button"
            onClick={handleCopyId}
            className="shrink-0 font-medium text-muted-foreground cursor-pointer rounded px-0.5 -mx-0.5 hover:bg-muted/60 hover:text-primary"
            aria-label={`Copy ${itemType} ID`}
          >
            ID
          </button>
        ) : null}
        {hasCopyableValue ? (
          <button
            type="button"
            onClick={handleCopyName}
            className="min-w-0 truncate text-left font-medium text-foreground/90 cursor-pointer rounded px-0.5 -mx-0.5 hover:bg-muted/60 hover:text-primary md:overflow-visible md:whitespace-nowrap"
            aria-label={`Copy ${itemType} name`}
          >
            {displayName}
          </button>
        ) : (
          <span
            data-no-select
            onClick={(e) => e.stopPropagation()}
            onPointerDown={(e) => e.stopPropagation()}
            className="min-w-0 truncate text-left font-medium text-foreground/90 md:overflow-visible md:whitespace-nowrap cursor-default"
          >
            {displayName}
          </span>
        )}
        {tagsLine}
        {flowTagsLine}
      </div>
    </StatsHoverPopover>
  );
};
